// src/pages/LearnPrayer.tsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import DonationSidebar from '../components/DonationSidebar';

const LearnPrayer: React.FC = () => {
  const [activeSection, setActiveSection] = useState<'basics' | 'daily' | 'special' | 'stepbystep'>('stepbystep');

  const prayerSteps = [
    {
      step: 1,
      title: "Make Wudu (Ablution)",
      description: "Perform the ritual purification before prayer",
      arabic: "",
      transliteration: "",
      translation: "",
      instructions: "Perform wudu by washing hands, mouth, nose, face, arms, head, ears, and feet as prescribed.",
      hasWuduButton: true
    },
    {
      step: 2,
      title: "Face the Qibla",
      description: "Turn towards the Kaaba in Mecca",
      arabic: "",
      transliteration: "",
      translation: "",
      instructions: "Stand facing the direction of the Kaaba. You can use a compass or Qibla finder app."
    },
    {
      step: 3,
      title: "Make Niyyah (Intention)",
      description: "Make intention in your heart",
      arabic: "",
      transliteration: "",
      translation: "",
      instructions: "Silently make the intention for which prayer you are performing (Fajr, Dhuhr, Asr, Maghrib, Isha)."
    },
    {
      step: 4,
      title: "Takbiratul Ihram",
      description: "Raise hands and say Allahu Akbar",
      arabic: "ٱللَّٰهُ أَكْبَرُ",
      transliteration: "Allahu Akbar",
      translation: "Allah is the Greatest",
      instructions: "Raise your hands to your ears and say 'Allahu Akbar' to begin the prayer."
    },
    {
      step: 5,
      title: "Standing (Qiyam) - Recite Opening Dua",
      description: "Stand and recite the opening supplication",
      arabic: "سُبْحَانَكَ ٱللَّٰهُمَّ وَبِحَمْدِكَ، وَتَبَارَكَ ٱسْمُكَ، وَتَعَالَىٰ جَدُّكَ، وَلَا إِلَٰهَ غَيْرُكَ",
      transliteration: "Subhanakallahumma wa bihamdika, wa tabarakasmuka, wa ta'ala jadduka, wa la ilaha ghairuk",
      translation: "Glory and praise be to You, O Allah. Blessed is Your name and exalted is Your majesty. There is no god but You.",
      instructions: "Place right hand over left hand on your chest and recite this opening dua."
    },
    {
      step: 6,
      title: "Recite Surah Al-Fatihah",
      description: "The opening chapter of Quran",
      arabic: "بِسْمِ ٱللَّٰهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ (1) ٱلْحَمْدُ لِلَّٰهِ رَبِّ ٱلْعَالَمِينَ (2) ٱلرَّحْمَٰنِ ٱلرَّحِيمِ (3) مَالِكِ يَوْمِ ٱلدِّينِ (4) إِيَّاكَ نَعْبُدُ وَإِيَّاكَ نَسْتَعِينُ (5) ٱهْدِنَا ٱلصِّرَاطَ ٱلْمُسْتَقِيمَ (6) صِرَاطَ ٱلَّذِينَ أَنْعَمْتَ عَلَيْهِمْ غَيْرِ ٱلْمَغْضُوبِ عَلَيْهِمْ وَلَا ٱلضَّالِّينَ (7)",
      transliteration: "Bismillahir Rahmanir Rahim. Alhamdu lillahi Rabbil 'alamin. Ar-Rahmanir Rahim. Maliki yawmidin. Iyyaka na'budu wa iyyaka nasta'in. Ihdinas siratal mustaqim. Siratal ladheena an'amta 'alaihim ghairil maghdubi 'alaihim wa lad-dallin.",
      translation: "In the name of Allah, the Entirely Merciful, the Especially Merciful. All praise is due to Allah, Lord of the worlds. The Entirely Merciful, the Especially Merciful. Sovereign of the Day of Recompense. It is You we worship and You we ask for help. Guide us to the straight path. The path of those upon whom You have bestowed favor, not of those who have earned Your anger or of those who are astray.",
      instructions: "Recite Surah Al-Fatihah completely. This is obligatory in every rak'ah."
    },
    {
      step: 7,
      title: "Recite Another Surah",
      description: "Recite any chapter from Quran",
      arabic: "بِسْمِ ٱللَّٰهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ قُلْ هُوَ ٱللَّٰهُ أَحَدٌ (1) ٱللَّٰهُ ٱلصَّمَدُ (2) لَمْ يَلِدْ وَلَمْ يُولَدْ (3) وَلَمْ يَكُن لَّهُۥ كُفُوًا أَحَدٌۢ (4)",
      transliteration: "Bismillahir Rahmanir Rahim. Qul huwallahu ahad. Allahus-samad. Lam yalid wa lam yulad. Wa lam yakun lahu kufuwan ahad.",
      translation: "In the name of Allah, the Entirely Merciful, the Especially Merciful. Say, 'He is Allah, the One. Allah, the Eternal Refuge. He neither begets nor is born. Nor is there to Him any equivalent.'",
      instructions: "Recite Surah Al-Ikhlas or any other short surah you have memorized.",
      hasSurahButton: true
    },
    {
      step: 8,
      title: "Ruku (Bowing)",
      description: "Bow down while saying Takbir",
      arabic: "سُبْحَانَ رَبِّيَ ٱلْعَظِيمِ",
      transliteration: "Subhana Rabbiyal Adheem",
      translation: "Glory to my Lord, the Most Great",
      instructions: "Say 'Allahu Akbar' while going into bowing position. Place hands on knees and recite this tasbih 3 times."
    },
    {
      step: 9,
      title: "Rising from Ruku",
      description: "Stand up from bowing position",
      arabic: "سَمِعَ ٱللَّٰهُ لِمَنْ حَمِدَهُ، رَبَّنَا وَلَكَ الْحَمْدُ، حَمْدًا كَثِيرًا طَيِّبًا مُبَارَكًا فِيهِ",
      transliteration: "Sami'Allahu liman hamidah, Rabbana wa lakal-ḥamd, ḥamdan kathīran ṭayyiban mubārakan fīh",
      translation: "Allah hears those who praise Him. Our Lord, and to You is all praise — an abundant, pure, and blessed praise.",
      instructions: "While rising, say 'Sami'Allahu liman hamidah' then 'Rabbana wa lakal-hamd' with the additional beautiful praise."
    },
    {
      step: 10,
      title: "Sujood (First Prostration)",
      description: "Prostrate on the ground",
      arabic: "سُبْحَانَ رَبِّيَ ٱلْأَعْلَىٰ",
      transliteration: "Subhana Rabbiyal A'la",
      translation: "Glory to my Lord, the Most High",
      instructions: "Say 'Allahu Akbar' while going into prostration. Place forehead, nose, palms, knees, and toes on ground. Recite this tasbih 3 times."
    },
    {
      step: 11,
      title: "Sitting Between Prostrations",
      description: "Sit briefly between two prostrations",
      arabic: "اللّٰهُمَّ اغْفِرْ لِي، وَارْحَمْنِي، وَعَافِنِي، وَاهْدِنِي، وَارْزُقْنِي",
      transliteration: "Allahummaghfir lī, warḥamnī, wa 'āfinī, wahdinī, warzuqnī",
      translation: "O Allah! Forgive me, have mercy on me, grant me well-being, guide me, and provide for me.",
      instructions: "Sit with right foot upright and left foot flat. Place hands on thighs and recite this comprehensive supplication."
    },
    {
      step: 12,
      title: "Second Sujood",
      description: "Second prostration",
      arabic: "سُبْحَانَ رَبِّيَ ٱلْأَعْلَىٰ",
      transliteration: "Subhana Rabbiyal A'la",
      translation: "Glory to my Lord, the Most High",
      instructions: "Perform second prostration exactly like the first one, reciting the same tasbih 3 times."
    },
    {
      step: 13,
      title: "Second Rak'ah",
      description: "Repeat steps for second unit",
      arabic: "",
      transliteration: "",
      translation: "",
      instructions: "Stand up for second rak'ah and repeat from Surah Al-Fatihah. After second sujood, remain seated for Tashahhud."
    },
    {
      step: 14,
      title: "Tashahhud (Sitting)",
      description: "Recite the testimony of faith",
      arabic: "ٱلتَّحِيَّاتُ لِلَّٰهِ وَٱلصَّلَوَاتُ وَٱلطَّيِّبَاتُ، ٱلسَّلَامُ عَلَيْكَ أَيُّهَا ٱلنَّبِيُّ وَرَحْمَةُ ٱللَّٰهِ وَبَرَكَاتُهُ، ٱلسَّلَامُ عَلَيْنَا وَعَلَىٰ عِبَادِ ٱللَّٰهِ ٱلصَّالِحِينَ، أَشْهَدُ أَنْ لَا إِلَٰهَ إِلَّا ٱللَّٰهُ، وَأَشْهَدُ أَنَّ مُحَمَّدًا عَبْدُهُ وَرَسُولُهُ",
      transliteration: "At-tahiyyatu lillahi was-salawatu wat-tayyibatu. As-salamu 'alaika ayyuhan-Nabiyyu wa rahmatullahi wa barakatuhu. As-salamu 'alaina wa 'ala 'ibadillahis-salihin. Ash-hadu an la ilaha illallah wa ash-hadu anna Muhammadan 'abduhu wa rasuluh.",
      translation: "All greetings, prayers, and pure words are for Allah. Peace be upon you, O Prophet, and the mercy of Allah and His blessings. Peace be upon us and upon the righteous servants of Allah. I bear witness that there is no god but Allah, and I bear witness that Muhammad is His servant and Messenger.",
      instructions: "Sit in the same position as between prostrations and recite Tashahhud."
    },
    {
      step: 15,
      title: "Durood Ibrahim",
      description: "Send blessings upon Prophet Muhammad",
      arabic: "ٱللَّٰهُمَّ صَلِّ عَلَىٰ مُحَمَّدٍ وَعَلَىٰ آلِ مُحَمَّدٍ، كَمَا صَلَّيْتَ عَلَىٰ إِبْرَاهِيمَ وَعَلَىٰ آلِ إِبْرَاهِيمَ، إِنَّكَ حَمِيدٌ مَجِيدٌ، ٱللَّٰهُمَّ بَارِكْ عَلَىٰ مُحَمَّدٍ وَعَلَىٰ آلِ مُحَمَّدٍ، كَمَا بَارَكْتَ عَلَىٰ إِبْرَاهِيمَ وَعَلَىٰ آلِ إِبْرَاهِيمَ، إِنَّكَ حَمِيدٌ مَجِيدٌ",
      transliteration: "Allahumma salli 'ala Muhammadin wa 'ala ali Muhammadin, kama sallayta 'ala Ibrahima wa 'ala ali Ibrahima, innaka Hamidum Majid. Allahumma barik 'ala Muhammadin wa 'ala ali Muhammadin, kama barakta 'ala Ibrahima wa 'ala ali Ibrahima, innaka Hamidum Majid.",
      translation: "O Allah, send prayers upon Muhammad and the family of Muhammad, as You sent prayers upon Ibrahim and the family of Ibrahim. You are indeed Praiseworthy, Glorious. O Allah, send blessings upon Muhammad and the family of Muhammad, as You sent blessings upon Ibrahim and the family of Ibrahim. You are indeed Praiseworthy, Glorious.",
      instructions: "Recite this after Tashahhud in the final sitting."
    },
    {
      step: 16,
      title: "Closing Dua",
      description: "Supplication before ending prayer",
      arabic: "ٱللَّٰهُمَّ إِنِّي أَعُوذُ بِكَ مِنْ عَذَابِ جَهَنَّمَ، وَمِنْ عَذَابِ ٱلْقَبْرِ، وَمِنْ فِتْنَةِ ٱلْمَحْيَا وَٱلْمَمَاتِ، وَمِنْ شَرِّ فِتْنَةِ ٱلْمَسِيحِ ٱلدَّجَّالِ",
      transliteration: "Allahumma inni a'udhu bika min 'adhabi jahannam, wa min 'adhabil-qabr, wa min fitnatil-mahya wal-mamat, wa min sharri fitnatil-masihid-dajjal.",
      translation: "O Allah, I seek refuge in You from the punishment of Hellfire, from the punishment of the grave, from the trials of life and death, and from the evil of the trial of the False Messiah.",
      instructions: "Recite this or any other authentic supplication before ending the prayer."
    },
    {
      step: 17,
      title: "Tasleem (Ending Prayer)",
      description: "End the prayer with salam",
      arabic: "ٱلسَّلَامُ عَلَيْكُمْ وَرَحْمَةُ ٱللَّٰهِ",
      transliteration: "Assalamu alaikum wa rahmatullah",
      translation: "Peace and mercy of Allah be upon you",
      instructions: "Turn your face to the right and say salam, then to the left and say salam to complete the prayer."
    }
  ];

  const prayerBasics = [
    {
      title: "Dua After Rising from Ruku (Before Going to Sujood)",
      arabic: "رَبَّنَا وَلَكَ الْحَمْدُ، حَمْدًا كَثِيرًا طَيِّبًا مُبَارَكًا فِيهِ",
      transliteration: "Rabbana wa lakal-ḥamd, ḥamdan kathīran ṭayyiban mubārakan fīh",
      translation: "Our Lord, and to You is all praise — an abundant, pure, and blessed praise.",
      reference: "Bukhari (no. 799)",
      explanation: "The Prophet ﷺ said: 'I saw thirty-some angels competing to write it first.' It's not just praise — it's an angel-compelling dua."
    },
    {
      title: "Dua Between the Two Sujoods (Sajdahs)",
      arabic: "اللّٰهُمَّ اغْفِرْ لِي، وَارْحَمْنِي، وَعَافِنِي، وَاهْدِنِي، وَارْزُقْنِي",
      transliteration: "Allahummaghfir lī, warḥamnī, wa 'āfinī, wahdinī, warzuqnī",
      translation: "O Allah! Forgive me, have mercy on me, grant me well-being, guide me, and provide for me.",
      reference: "Abu Dawud (850), Ibn Majah (898), Sahih (Albani)",
      explanation: "It combines five essential needs: forgiveness, mercy, health, guidance, and provision — basically, everything a believer needs in dunya and akhirah, in one line."
    }
  ];

  const dailyPrayers = [
    {
      name: "Fajr (Dawn Prayer)",
      rakaat: "2 Sunnah Mu'akkadah + 2 Farz",
      time: "From dawn (Fajr) until just before sunrise",
      status: "Farz (obligatory)",
      importance: "Prophet ﷺ said: 'The two rak'ahs of Fajr are better than the world and all that it contains.' (Muslim 725)",
      benefit: "Builds discipline, starts the day with purity and remembrance"
    },
    {
      name: "Dhuhr (Noon Prayer)",
      rakaat: "4 Sunnah Mu'akkadah + 4 Farz + 2 Sunnah + (2 Nafl optional)",
      time: "After the sun passes zenith until before Asr",
      status: "Farz",
      importance: "Allah says: 'Establish the prayer at the decline of the sun until the darkness of the night.' (Qur'an 17:78)",
      benefit: "Encourages a mid-day pause for mindfulness and gratitude"
    },
    {
      name: "Asr (Afternoon Prayer)",
      rakaat: "4 Sunnah Ghair Mu'akkadah + 4 Farz",
      time: "From when shadow of an object is equal to its length until before sunset",
      status: "Farz",
      importance: "Prophet ﷺ said: 'Whoever misses the Asr prayer, it is as if he lost his family and wealth.' (Bukhari 552)",
      benefit: "Protects one's day from heedlessness and laziness"
    },
    {
      name: "Maghrib (Sunset Prayer)",
      rakaat: "3 Farz + 2 Sunnah Mu'akkadah + (2 Nafl optional)",
      time: "Immediately after sunset until twilight disappears",
      status: "Farz",
      importance: "'So glorify your Lord when you reach the evening and when you rise in the morning.' (Qur'an 30:17)",
      benefit: "Balances worldly duties with gratitude at day's end"
    },
    {
      name: "Isha (Night Prayer)",
      rakaat: "4 Sunnah Ghair Mu'akkadah + 4 Farz + 2 Sunnah + 3 Witr Wajib + (2 Nafl optional)",
      time: "From disappearance of twilight until Fajr",
      status: "Farz (and Witr is Wajib)",
      importance: "Prophet ﷺ said: 'Whoever prays Isha in congregation, it is as if he spent half the night in prayer.' (Muslim 656)",
      benefit: "Cleanses the day, prepares soul for peace and sleep"
    }
  ];

  const specialPrayers = [
    {
      name: "Tahajjud (Night Prayer)",
      rakaat: "Minimum 2 (no fixed limit)",
      time: "After Isha and before Fajr (best in last third of night)",
      status: "Sunnah Mu'akkadah (highly recommended)",
      importance: "'And during part of the night, pray Tahajjud as an extra for you; it may be that your Lord will raise you to a praised station.' (Qur'an 17:79)",
      benefit: "Deep connection with Allah, answered duas, forgiveness, tranquility"
    },
    {
      name: "Witr (Odd Prayer)",
      rakaat: "3 (with one salaam at the end)",
      time: "After Isha until Fajr",
      status: "Wajib (necessary)",
      importance: "Prophet ﷺ said: 'Witr is a duty upon every Muslim.' (Abu Dawud 1416, Nasai 1679)",
      benefit: "Completes the night prayers; seals the day's worship beautifully"
    },
    {
      name: "Tarawih (Ramadan Night Prayer)",
      rakaat: "8 to 20",
      time: "After Isha during Ramadan",
      status: "Sunnah Mu'akkadah in Ramadan",
      importance: "Prophet ﷺ said: 'Whoever stands (in prayer) during Ramadan with faith and seeking reward, his past sins will be forgiven.' (Bukhari 37)",
      benefit: "Cleanses sins and builds spiritual focus in Ramadan"
    },
    {
      name: "Jumu'ah (Friday Prayer)",
      rakaat: "2 Farz (congregational) + 4 Sunnah before + 4 after (recommended)",
      time: "Replaces Dhuhr every Friday",
      status: "Farz for adult males",
      importance: "'O you who believe! When the call is proclaimed for Jumu'ah, hasten to the remembrance of Allah and leave trade.' (Qur'an 62:9)",
      benefit: "Weekly purification and unity of the Ummah"
    },
    {
      name: "Salat al-Istikhara (Prayer for Guidance)",
      rakaat: "2 Nafl",
      time: "Any time except prohibited times (before Fajr, after Asr)",
      status: "Sunnah",
      importance: "Special dua for seeking Allah's guidance in decisions",
      benefit: "Seek clarity in decision-making; Allah guides toward what's best"
    },
    {
      name: "Eid Prayer (Eid al-Fitr & Eid al-Adha)",
      rakaat: "2 (with 6 extra Takbeers)",
      time: "After sunrise (about 15–20 minutes after Fajr time) until Dhuhr",
      status: "Wajib Kifayah (communal obligation)",
      importance: "Prophet ﷺ never missed Eid Salah. (Bukhari 956)",
      benefit: "Expression of gratitude, unity, and joy after major acts of worship"
    },
    {
      name: "Janazah (Funeral Prayer)",
      rakaat: "None; consists of Takbeerat (4) and supplications standing",
      time: "Any time except during sunrise, zenith, or sunset",
      status: "Fard Kifayah (communal obligation)",
      importance: "Prophet ﷺ said: 'Whoever prays Janazah will have one Qirat of reward, like Mount Uhud.' (Bukhari 1325)",
      benefit: "Intercession for the deceased, massive reward for attendees"
    }
  ];

  return (
    <div className="section-padding">
      <div className="container">
        <div className="section-title">
          <h1>Learn How to Pray (Salah)</h1>
          <p>Complete step-by-step guide to performing all prayers with authentic duas and instructions</p>
        </div>

        {/* Navigation Tabs */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '40px',
          borderBottom: '1px solid var(--border)',
          flexWrap: 'wrap',
          gap: '8px'
        }}>
          <button
            onClick={() => setActiveSection('stepbystep')}
            style={{
              padding: '12px 24px',
              border: 'none',
              backgroundColor: activeSection === 'stepbystep' ? 'var(--accent-primary)' : 'transparent',
              color: activeSection === 'stepbystep' ? 'var(--bg-primary)' : 'var(--text-secondary)',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              borderBottom: activeSection === 'stepbystep' ? '2px solid var(--accent-primary)' : 'none',
              borderRadius: '8px 8px 0 0'
            }}
          >
            📖 Step-by-Step Prayer
          </button>
          <button
            onClick={() => setActiveSection('basics')}
            style={{
              padding: '12px 24px',
              border: 'none',
              backgroundColor: activeSection === 'basics' ? 'var(--accent-primary)' : 'transparent',
              color: activeSection === 'basics' ? 'var(--bg-primary)' : 'var(--text-secondary)',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              borderBottom: activeSection === 'basics' ? '2px solid var(--accent-primary)' : 'none',
              borderRadius: '8px 8px 0 0'
            }}
          >
            🕋 Prayer Duas
          </button>
          <button
            onClick={() => setActiveSection('daily')}
            style={{
              padding: '12px 24px',
              border: 'none',
              backgroundColor: activeSection === 'daily' ? 'var(--accent-primary)' : 'transparent',
              color: activeSection === 'daily' ? 'var(--bg-primary)' : 'var(--text-secondary)',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              borderBottom: activeSection === 'daily' ? '2px solid var(--accent-primary)' : 'none',
              borderRadius: '8px 8px 0 0'
            }}
          >
            🌅 Daily Prayers
          </button>
          <button
            onClick={() => setActiveSection('special')}
            style={{
              padding: '12px 24px',
              border: 'none',
              backgroundColor: activeSection === 'special' ? 'var(--accent-primary)' : 'transparent',
              color: activeSection === 'special' ? 'var(--bg-primary)' : 'var(--text-secondary)',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              borderBottom: activeSection === 'special' ? '2px solid var(--accent-primary)' : 'none',
              borderRadius: '8px 8px 0 0'
            }}
          >
            🌟 Special Prayers
          </button>
        </div>

        {/* Step-by-Step Prayer Guide */}
        {activeSection === 'stepbystep' && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <h2 style={{ color: 'var(--text-primary)', marginBottom: '16px' }}>📖 Complete Step-by-Step Prayer Guide</h2>
              <p style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
                Learn how to perform Salah correctly with all the essential duas integrated at the proper steps.
              </p>
            </div>
            
            <div className="space-y-6">
              {prayerSteps.map((step) => (
                <div key={step.step} className="card">
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                    <div style={{
                      flexShrink: 0,
                      width: '48px',
                      height: '48px',
                      backgroundColor: 'var(--accent-primary)',
                      color: 'var(--bg-primary)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: '16px'
                    }}>
                      {step.step}
                    </div>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ 
                        marginBottom: '8px', 
                        color: 'var(--text-primary)',
                        fontSize: '1.25rem'
                      }}>
                        {step.title}
                      </h3>
                      <p style={{ 
                        color: 'var(--text-secondary)', 
                        marginBottom: '12px',
                        fontSize: '14px'
                      }}>
                        {step.description}
                      </p>
                      
                      {step.instructions && (
                        <div style={{
                          backgroundColor: 'var(--bg-secondary)',
                          padding: '12px 16px',
                          borderRadius: '8px',
                          marginBottom: '12px'
                        }}>
                          <p style={{ 
                            fontSize: '14px', 
                            color: 'var(--text-secondary)',
                            margin: 0
                          }}>
                            {step.instructions}
                          </p>
                        </div>
                      )}

                      {/* Wudu Button in Step 1 */}
                      {step.hasWuduButton && (
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'center', 
                          margin: '16px 0'
                        }}>
                          <Link 
                            to="/wudu-guide" 
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '12px 20px',
                              backgroundColor: 'var(--accent-primary)',
                              color: 'var(--bg-primary)',
                              textDecoration: 'none',
                              borderRadius: '8px',
                              fontWeight: '600',
                              fontSize: '14px',
                              transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = 'none';
                            }}
                          >
                            🌙 Learn Complete Wudu Guide
                          </Link>
                        </div>
                      )}

                      {/* Second Surah Button in Step 7 */}
                      {step.hasSurahButton && (
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'center', 
                          margin: '16px 0'
                        }}>
                          <Link 
                            to="/quran" 
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '12px 20px',
                              backgroundColor: '#22c55e',
                              color: 'var(--bg-primary)',
                              textDecoration: 'none',
                              borderRadius: '8px',
                              fontWeight: '600',
                              fontSize: '14px',
                              transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = 'none';
                            }}
                          >
                            📖 Choose Your Second Surah from Quran
                          </Link>
                        </div>
                      )}

                      {step.arabic && (
                        <div style={{ marginTop: '16px' }}>
                          <div style={{ textAlign: 'right', marginBottom: '12px' }}>
                            <p style={{ 
                              fontSize: '20px', 
                              lineHeight: '1.8',
                              fontFamily: "'Amiri', 'Traditional Arabic', serif",
                              direction: 'rtl'
                            }}>
                              {step.arabic}
                            </p>
                          </div>
                          <div style={{ 
                            backgroundColor: 'var(--bg-secondary)',
                            padding: '12px 16px',
                            borderRadius: '8px'
                          }}>
                            <p style={{ 
                              color: 'var(--accent-primary)', 
                              fontWeight: '500',
                              marginBottom: '4px',
                              fontSize: '14px'
                            }}>
                              {step.transliteration}
                            </p>
                            <p style={{ 
                              color: 'var(--text-secondary)', 
                              fontStyle: 'italic',
                              margin: 0,
                              fontSize: '14px'
                            }}>
                              "{step.translation}"
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Important Notes */}
            <div className="card" style={{ 
              marginTop: '32px',
              backgroundColor: 'var(--bg-secondary)',
              border: '1px solid var(--accent-primary)'
            }}>
              <h3 style={{ 
                color: 'var(--text-primary)', 
                marginBottom: '16px',
                fontSize: '1.125rem'
              }}>
                📝 Important Notes:
              </h3>
              <ul style={{ 
                color: 'var(--text-secondary)',
                paddingLeft: '20px',
                margin: 0
              }}>
                <li style={{ marginBottom: '8px' }}>• This guide covers a 2-rak'ah prayer. For 3 or 4 rak'ah prayers, repeat the standing-bowing-prostration sequence</li>
                <li style={{ marginBottom: '8px' }}>• Men and women have slight differences in hand positions and sitting postures</li>
                <li style={{ marginBottom: '8px' }}>• Always maintain focus and humility during prayer</li>
                <li style={{ marginBottom: '8px' }}>• Learn the Arabic pronunciations correctly from a qualified teacher</li>
                <li>• The duas are integrated at their proper positions in the prayer sequence</li>
              </ul>
            </div>
          </div>
        )}

        {/* Prayer Basics & Duas Section */}
        {activeSection === 'basics' && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <h2 style={{ color: 'var(--text-primary)', marginBottom: '16px' }}>🕋 Essential Prayer Duas</h2>
              <p style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
                Learn the powerful supplications that the Prophet ﷺ taught us to recite during prayer.
              </p>
            </div>
            
            <div className="grid-2">
              {prayerBasics.map((dua, index) => (
                <div key={index} className="card">
                  <h3 style={{ marginBottom: '16px', color: 'var(--text-primary)' }}>
                    {dua.title}
                  </h3>
                  <div className="arabic" style={{
                    fontSize: '20px',
                    lineHeight: '1.8',
                    marginBottom: '12px',
                    textAlign: 'right',
                    direction: 'rtl',
                    fontFamily: "'Amiri', 'Traditional Arabic', serif"
                  }}>
                    {dua.arabic}
                  </div>
                  <div style={{
                    color: 'var(--accent-primary)',
                    fontSize: '14px',
                    fontStyle: 'italic',
                    marginBottom: '8px'
                  }}>
                    {dua.transliteration}
                  </div>
                  <div style={{
                    color: 'var(--text-secondary)',
                    fontSize: '14px',
                    marginBottom: '8px',
                    lineHeight: '1.6'
                  }}>
                    {dua.translation}
                  </div>
                  <div style={{
                    color: 'var(--accent-primary)',
                    fontSize: '13px',
                    marginBottom: '8px',
                    fontStyle: 'italic',
                    backgroundColor: 'var(--bg-secondary)',
                    padding: '12px',
                    borderRadius: '8px'
                  }}>
                    💡 {dua.explanation}
                  </div>
                  <div style={{
                    color: 'var(--text-secondary)',
                    fontSize: '12px',
                    opacity: '0.7'
                  }}>
                    {dua.reference}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Daily Prayers Section */}
        {activeSection === 'daily' && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <h2 style={{ color: 'var(--text-primary)', marginBottom: '16px' }}>🌅 The Five Daily Prayers</h2>
              <p style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
                Complete guide to the obligatory prayers that every Muslim must perform daily.
              </p>
            </div>
            
            <div className="grid-2">
              {dailyPrayers.map((prayer, index) => (
                <div key={index} className="card">
                  <h3 style={{ marginBottom: '16px', color: 'var(--text-primary)' }}>
                    {prayer.name}
                  </h3>
                  <div style={{ marginBottom: '12px' }}>
                    <strong>Raka'at:</strong> <span style={{ color: 'var(--text-secondary)' }}>{prayer.rakaat}</span>
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <strong>Time:</strong> <span style={{ color: 'var(--text-secondary)' }}>{prayer.time}</span>
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <strong>Status:</strong> <span style={{ color: 'var(--accent-primary)' }}>{prayer.status}</span>
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <strong>Importance:</strong> <span style={{ color: 'var(--text-secondary)' }}>{prayer.importance}</span>
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <strong>Benefit:</strong> <span style={{ color: 'var(--text-secondary)' }}>{prayer.benefit}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Special Prayers Section */}
        {activeSection === 'special' && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <h2 style={{ color: 'var(--text-primary)', marginBottom: '16px' }}>🌟 Special & Optional Prayers</h2>
              <p style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
                Additional prayers that bring extra rewards and spiritual benefits.
              </p>
            </div>
            
            <div className="grid-2">
              {specialPrayers.map((prayer, index) => (
                <div key={index} className="card">
                  <h3 style={{ marginBottom: '16px', color: 'var(--text-primary)' }}>
                    {prayer.name}
                  </h3>
                  <div style={{ marginBottom: '12px' }}>
                    <strong>Raka'at:</strong> <span style={{ color: 'var(--text-secondary)' }}>{prayer.rakaat}</span>
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <strong>Time:</strong> <span style={{ color: 'var(--text-secondary)' }}>{prayer.time}</span>
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <strong>Status:</strong> <span style={{ color: 'var(--accent-primary)' }}>{prayer.status}</span>
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <strong>Importance:</strong> <span style={{ color: 'var(--text-secondary)' }}>{prayer.importance}</span>
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <strong>Benefit:</strong> <span style={{ color: 'var(--text-secondary)' }}>{prayer.benefit}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Donation Sidebar */}
        <DonationSidebar />
      </div>
    </div>
  );
};

export default LearnPrayer;